import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMoon, FaHome, FaChartLine, FaRoad, FaLandmark, FaCrown, FaBars, FaBookOpen, FaMicrophoneAlt, FaCheck, FaStar, FaUser, FaArrowRight, FaPaperPlane, FaInstagram, FaTwitter, FaYoutube, FaTelegram, FaStarHalfAlt, FaShieldAlt } from 'react-icons/fa';
import SEO from '../components/SEO';

const Pricing: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Helper for floating animation delay
  const floatingStyle = { animation: 'float 3s ease-in-out infinite', animationDelay: `${Math.random() * 2}s` };

  return (
    <>
      <SEO 
        title="Pricing Plans | پیروز Pirooz - Affordable Farsi Learning"
        description="Choose the perfect plan to learn Farsi with پیروز Pirooz. Free and premium options available. Start learning Persian language today with our affordable pricing plans."
        keywords={[
          'farsi learning price', 'persian course cost', 'farsi lessons pricing', 'learn farsi affordable',
          'persian language subscription', 'farsi course plans', 'budget farsi learning'
        ]}
        canonicalUrl="/pricing"
      />
      <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <FaMoon className="text-indigo-600 text-2xl mr-2" />
                <span className="text-xl font-bold text-gray-900">Pirooz</span>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link to="/" className="nav-link border-indigo-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200">
                  <FaHome className="mr-1" /> Home
                </Link>
                <Link to="/dashboard" className="nav-link border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200">
                  <FaChartLine className="mr-1" /> Dashboard
                </Link>
                <Link to="/pathways" className="nav-link border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200">
                  <FaRoad className="mr-1" /> Pathways
                </Link>
                <Link to="/cultural" className="nav-link border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200">
                  <FaLandmark className="mr-1" /> Cultural
                </Link>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              <div className="relative">
                <Link to="/subscription" className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 flex items-center">
                  <FaCrown className="mr-2" /> Try Premium
                </Link>
              </div>
            </div>
            <div className="-mr-2 flex items-center sm:hidden">
              <button type="button" className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500" aria-controls="mobile-menu" aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span className="sr-only">Open main menu</span>
                <FaBars />
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        <div className={`sm:hidden${mobileMenuOpen ? '' : ' hidden'}`} id="mobile-menu">
          <div className="pt-2 pb-3 space-y-1">
            <Link to="/" className="bg-indigo-50 border-indigo-500 text-indigo-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">Home</Link>
            <Link to="/dashboard" className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">Dashboard</Link>
            <Link to="/pathways" className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">Pathways</Link>
            <Link to="/cultural" className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">Cultural</Link>
            <Link to="/subscription" className="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">Try Premium</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="gradient-bg text-white" style={{background: 'linear-gradient(135deg, #6B73FF 0%, #000DFF 50%, #0085FF 100%)'}}>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 lg:py-24">
          <div className="text-left">
            <div className="mb-8">
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl mb-4 text-white">
                Pirooz <span className="farsi-text" style={{fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif', color: 'white'}}>پیروز</span>
              </h1>
              <p className="text-xl text-indigo-100 max-w-3xl">
                Master Farsi vocabulary with our beautiful, intuitive platform. Discover the poetry of Persian language through immersive learning.
              </p>
              <div className="mt-8">
                <div className="inline-flex rounded-md shadow">
                  <Link to="/subscription" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 transition-all duration-200">
                    Start 7-Day Free Trial <FaArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Anytime Policy Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left">
            <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4">
              <FaShieldAlt className="text-white text-3xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-2">
                Cancel Anytime, No Questions Asked
              </h2>
              <p className="text-green-100 text-sm sm:text-base">
                Your subscription can be cancelled at any time. You'll continue to have access until the end of your current billing period.
              </p>
            </div>
            <div className="flex-shrink-0 mt-4 sm:mt-0 sm:ml-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <FaCheck className="text-white text-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              A beautiful way to learn Farsi
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500">
              Our platform combines modern learning techniques with the elegance of Persian culture.
            </p>
          </div>
          <div className="mt-10">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
              <div className="card-hover bg-white p-6 rounded-lg shadow-md transition-all duration-300">
                <div className="feature-icon bg-indigo-50 text-indigo-600 flex items-center justify-center rounded-[12px] mb-4" style={{width: 50, height: 50}}>
                  <FaBookOpen className="text-xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Cultural Pathways</h3>
                <p className="text-gray-500">Learn vocabulary through Persian poetry, literature, and cultural contexts.</p>
              </div>
              <div className="card-hover bg-white p-6 rounded-lg shadow-md transition-all duration-300">
                <div className="feature-icon bg-purple-50 text-purple-600 flex items-center justify-center rounded-[12px] mb-4" style={{width: 50, height: 50}}>
                  <FaMoon className="text-xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Moonlight Mode</h3>
                <p className="text-gray-500">Study with our soothing night mode featuring Persian miniature art.</p>
              </div>
              <div className="card-hover bg-white p-6 rounded-lg shadow-md transition-all duration-300">
                <div className="feature-icon bg-green-50 text-green-600 flex items-center justify-center rounded-[12px] mb-4" style={{width: 50, height: 50}}>
                  <FaMicrophoneAlt className="text-xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Pronunciation</h3>
                <p className="text-gray-500">Perfect your accent with native speaker audio recordings.</p>
              </div>
              <div className="card-hover bg-white p-6 rounded-lg shadow-md transition-all duration-300">
                <div className="feature-icon bg-yellow-50 text-yellow-600 flex items-center justify-center rounded-[12px] mb-4" style={{width: 50, height: 50}}>
                  <FaChartLine className="text-xl" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Progress Tracking</h3>
                <p className="text-gray-500">Visualize your learning journey with beautiful analytics.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Pricing</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">Choose your learning path</p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500">Flexible plans that grow with your Farsi learning journey.</p>
          </div>
          <div className="mt-10 space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8">
            {/* Free Tier */}
            <div className="pricing-card bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
                <p className="text-gray-600 mb-6">Great for getting started</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Basic courses</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Community forum access</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Limited practice exercises</span></li>
                </ul>
              </div>
              <div>
                <Link to="/register" className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-all duration-200 block text-center">Get Started</Link>
              </div>
            </div>
            {/* Premium Tier */}
            <div className="pricing-card premium-card bg-white p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-indigo-600 relative overflow-hidden transform hover:scale-105">
              <div className="flex-1">
                <div className="absolute -top-4 right-6 bg-indigo-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg">MOST POPULAR</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium</h3>
                <p className="text-gray-600 mb-6">For advanced learners</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$9.99</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">All Free features</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Advanced & specialized courses</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Two 30-minute tutoring sessions/month</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Personalized learning plan</span></li>
                  <li className="flex items-start text-gray-700"><FaCheck className="text-green-500 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Priority support</span></li>
                </ul>
              </div>
              <div>
                <Link to="/subscription" className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-all duration-200 block text-center shadow-lg">Choose Premium</Link>
              </div>
            </div>
            {/* Platinum Tier */}
            <div className="pricing-card platinum-card p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white', position: 'relative', overflow: 'hidden'}}>
              <div className="flex-1">
                <div className="absolute -top-4 right-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 text-sm font-bold px-6 py-2 rounded-full shadow-lg">PREMIUM</div>
                <h3 className="text-2xl font-bold text-white mb-2">Platinum</h3>
                <p className="text-blue-100 mb-6">Ultimate experience for serious learners</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$24.99</span>
                  <span className="text-blue-100">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Everything in Premium</span></li>
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Unlimited tutoring sessions</span></li>
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">1-on-1 coaching with native speakers</span></li>
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Exclusive cultural events & webinars</span></li>
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Certificate of completion</span></li>
                  <li className="flex items-start text-white"><FaCheck className="text-green-400 mt-1 mr-3 flex-shrink-0" /><span className="font-medium">Direct support from Vajak team</span></li>
                </ul>
              </div>
              <div>
                <Link to="/subscription" className="w-full bg-white text-gray-900 py-3 px-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 block text-center shadow-lg">Go Platinum</Link>
              </div>
            </div>
          </div>
          <div className="mt-12 text-left">
            <p className="text-gray-500">All plans come with a 7-day free trial. Cancel anytime during your trial and you won't be charged.</p>
            <div className="mt-6">
              <Link to="/subscription" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200">
                Start 7-Day Free Trial
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Testimonials</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">What our learners say</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FaUser className="text-indigo-600 text-xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Sarah M.</h4>
                  <div className="flex text-yellow-400">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic">"Pirooz Changed how I learn Farsi. The cultural context makes vocabulary stick in my memory. I can now read Hafez with much more understanding!"</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <FaUser className="text-purple-600 text-xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Amir K.</h4>
                  <div className="flex text-yellow-400">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic">"As a heritage speaker, I wanted to improve my formal Farsi. Vajak's personalized approach helped me bridge the gap between my home language and literary Persian."</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FaUser className="text-green-600 text-xl" />
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Leila R.</h4>
                  <div className="flex text-yellow-400">
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStarHalfAlt />
                  </div>
                </div>
              </div>
              <p className="text-gray-600 italic">"The Moonlight in a Teacup feature is magical! Learning vocabulary through poetry at night has become my favorite ritual. I've learned more in 3 months than in a year of traditional study."</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-indigo-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8 lg:py-24">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div className="lg:col-span-1">
              <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Ready to begin your Farsi journey?</h2>
              <p className="mt-3 text-lg text-indigo-200">Join thousands of learners discovering the beauty of Persian language and culture.</p>
            </div>
            <div className="mt-8 lg:mt-0 lg:col-span-1">
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/subscription" className="flex-1 bg-white text-indigo-600 px-6 py-3 rounded-md text-base font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"><FaCrown className="mr-2" /> Try Premium Free</Link>
                <Link to="/pathways" className="flex-1 bg-indigo-800 text-white px-6 py-3 rounded-md text-base font-medium hover:bg-indigo-900 transition-all duration-200 flex items-center justify-center"><FaBookOpen className="mr-2" /> Explore Free Lessons</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white text-sm font-semibold tracking-wider uppercase mb-4">Learn</h3>
              <ul className="space-y-2">
                <li><Link to="/pathways" className="text-gray-300 hover:text-white transition-all duration-200">Pathways</Link></li>
                <li><Link to="/flashcards" className="text-gray-300 hover:text-white transition-all duration-200">Flashcards</Link></li>
                <li><Link to="/word-of-the-day" className="text-gray-300 hover:text-white transition-all duration-200">Word of the Day</Link></li>
                <li><Link to="/quiz" className="text-gray-300 hover:text-white transition-all duration-200">Quizzes</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold tracking-wider uppercase mb-4">Culture</h3>
              <ul className="space-y-2">
                <li><Link to="/hafez" className="text-gray-300 hover:text-white transition-all duration-200">Poetry</Link></li>
                <li><Link to="/cultural" className="text-gray-300 hover:text-white transition-all duration-200">Literature</Link></li>
                <li><Link to="/cultural/holidays" className="text-gray-300 hover:text-white transition-all duration-200">History</Link></li>
                <li><Link to="/cultural/recipes" className="text-gray-300 hover:text-white transition-all duration-200">Art</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold tracking-wider uppercase mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-all duration-200">About</Link></li>
                <li><Link to="/blog" className="text-gray-300 hover:text-white transition-all duration-200">Blog</Link></li>
                <li><Link to="/carreers" className="text-gray-300 hover:text-white transition-all duration-200">Careers</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-all duration-200">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold tracking-wider uppercase mb-4">Connect</h3>
              <div className="flex space-x-4">
                <a href="https://twitter.com/pirooz" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-200"><FaTwitter className="text-lg" /></a>
                <a href="https://instagram.com/pirooz" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-200"><FaInstagram className="text-lg" /></a>
                <a href="https://youtube.com/pirooz" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-200"><FaYoutube className="text-lg" /></a>
                <a href="https://t.me/pirooz" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-all duration-200"><FaTelegram className="text-lg" /></a>
              </div>
              <div className="mt-4">
                <p className="text-gray-300 text-sm">Subscribe to our newsletter for weekly Persian words and cultural insights.</p>
                <div className="mt-2 flex">
                  <input type="email" placeholder="Your email" className="px-3 py-2 rounded-l-md text-sm w-full" />
                  <button className="bg-indigo-600 text-white px-3 py-2 rounded-r-md text-sm hover:bg-indigo-700 transition-all duration-200"><FaPaperPlane /></button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between">
            <p className="text-gray-400 text-sm">&copy; 2025 Pirooz Farsi Vocabulary Builder. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-all duration-200">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-all duration-200">Terms</Link>
              <Link to="/cookies" className="text-gray-400 hover:text-white text-sm transition-all duration-200">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
      {/* Floating animation keyframes */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </div>
    </>
  );
};

export default Pricing; 